/**
 * avatarAnalyticsClient.ts
 *
 * Browser-side, fire-and-forget producer for the @camlin/analytics pipeline
 * (Phase P3). The avatar SDK can't write to the analytics sink directly (it's
 * server-side), so it POSTs CamlinEvent envelopes to the NextGen runtime ingest
 * endpoint `POST {runtimeUrl}/api/analytics/events`.
 *
 * The envelope shape is declared LOCALLY (not imported from
 * `@camlin/canonical-kit`) so the SDK stays dependency-free for its external
 * consumers (AgentLink, ClawLink, Decision Memory). The runtime validates every
 * event server-side anyway. The fields here mirror the CamlinEvent input shape.
 *
 * Discipline:
 *   - Never throws. A failed POST drops the batch; analytics must never break
 *     the avatar.
 *   - Batches + flushes on a short timer; uses `keepalive` so a flush survives
 *     page unload / navigation (the common case for short Visual IVR sessions).
 *   - No PII. Only types, severities, counts, and durations.
 */

export interface AvatarAnalyticsEvent {
  /** Stable idempotency key the sink dedupes on. */
  eventId: string;
  /** Dotted type, e.g. `avatar.health`, `avatar.live.session.started`. */
  type: string;
  ts?: number;
  /** Forced to `avatar-sdk` by the client. */
  source?: string;
  severity?: 'info' | 'ok' | 'warn' | 'error' | 'fail';
  tenantId?: string;
  sessionId?: string;
  channel?: 'voice' | 'chat' | 'avatar' | 'sms' | 'visual' | 'system';
  projectToken?: string;
  diagramId?: string;
  nodeId?: string;
  props?: Record<string, string | number | boolean | null>;
  metrics?: Record<string, number>;
}

const MAX_QUEUE = 200;
const FLUSH_AT = 10;
const FLUSH_INTERVAL_MS = 3000;

/** Build the ingest URL from a runtime base URL. Returns '' when unusable. */
export function buildAvatarIngestUrl(runtimeUrl: string | null | undefined): string {
  if (!runtimeUrl) return '';
  const trimmed = runtimeUrl.replace(/\/+$/, '');
  if (!trimmed) return '';
  return `${trimmed}/api/analytics/events`;
}

export class AvatarAnalyticsClient {
  private queue: AvatarAnalyticsEvent[] = [];
  private timer: ReturnType<typeof setTimeout> | null = null;
  private readonly ingestUrl: string;

  constructor(runtimeUrl: string | null | undefined) {
    this.ingestUrl = buildAvatarIngestUrl(runtimeUrl);
  }

  get enabled(): boolean {
    return this.ingestUrl.length > 0 && typeof fetch === 'function';
  }

  emit(event: AvatarAnalyticsEvent): void {
    if (!this.enabled || !event) return;
    try {
      this.queue.push({ ...event, source: event.source ?? 'avatar-sdk' });
      if (this.queue.length > MAX_QUEUE) {
        this.queue = this.queue.slice(this.queue.length - MAX_QUEUE);
      }
      if (this.queue.length >= FLUSH_AT) {
        void this.flush();
      } else {
        this.scheduleFlush();
      }
    } catch {
      /* never throw on the render path */
    }
  }

  private scheduleFlush(): void {
    if (this.timer) return;
    this.timer = setTimeout(() => {
      void this.flush();
    }, FLUSH_INTERVAL_MS);
  }

  async flush(): Promise<void> {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (this.queue.length === 0 || !this.enabled) return;
    const batch = this.queue.splice(0, MAX_QUEUE);
    try {
      await fetch(this.ingestUrl, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ events: batch }),
        // Survive page unload — Visual IVR sessions are often short-lived.
        keepalive: true,
      });
    } catch {
      // Best-effort: drop on failure rather than retry forever.
    }
  }
}

export default AvatarAnalyticsClient;
